import React from 'react';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../utils/cn';
import type { SkeletonAnimation } from '../ui/Skeleton/Skeleton.types';

interface ServiceCardSkeletonProps {
  /**
   * Layout variant matching ServiceCard
   * @default 'vertical'
   */
  layout?: 'vertical' | 'horizontal';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Animation type
   * @default 'pulse'
   */
  animation?: SkeletonAnimation;
}

/**
 * Skeleton placeholder for ServiceCard component
 *
 * Matches the exact layout of the ServiceCard including:
 * - Image area with category badge
 * - Title and meta info
 * - Price
 * - Description
 * - Action buttons
 */
export const ServiceCardSkeleton: React.FC<ServiceCardSkeletonProps> = ({
  layout = 'vertical',
  className,
  animation = 'pulse',
}) => {
  const isHorizontal = layout === 'horizontal';

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden',
        isHorizontal && 'flex flex-row',
        className
      )}
    >
      {/* Image Section */}
      <div
        className={cn(
          'relative overflow-hidden',
          isHorizontal ? 'w-48 flex-shrink-0 aspect-square' : 'aspect-[4/3]'
        )}
      >
        <Skeleton className="w-full h-full" variant="rectangular" animation={animation} />
        {/* Category Badge Placeholder */}
        <div className="absolute left-3 top-3">
          <Skeleton className="h-6 w-20" variant="rectangular" animation={animation} />
        </div>
      </div>

      {/* Content Section */}
      <div className={cn('flex flex-col p-4', isHorizontal ? 'flex-1' : '')}>
        {/* Title */}
        <div className="mb-2">
          <Skeleton className="h-6 w-3/4" animation={animation} />
        </div>

        {/* Meta Info (duration, therapist count) */}
        <div className={cn('flex gap-4', isHorizontal ? 'mb-2' : 'mb-3')}>
          <Skeleton className="h-4 w-20" animation={animation} />
          <Skeleton className="h-4 w-24" animation={animation} />
        </div>

        {/* Price */}
        <div className="mb-3">
          <Skeleton className="h-7 w-16" animation={animation} />
        </div>

        {/* Description */}
        <div className="space-y-2 flex-grow">
          <Skeleton className="h-4 w-full" animation={animation} />
          <Skeleton className="h-4 w-full" animation={animation} />
          <Skeleton className="h-4" width={isHorizontal ? '66%' : '80%'} animation={animation} />
        </div>

        {/* Action Buttons */}
        <div className={cn('flex gap-2 mt-4', isHorizontal ? 'flex-row' : 'flex-col sm:flex-row')}>
          <Skeleton
            className={cn('h-9', isHorizontal ? 'flex-1' : 'w-full sm:flex-1')}
            variant="rectangular"
            animation={animation}
          />
          <Skeleton
            className={cn('h-9', isHorizontal ? 'flex-1' : 'w-full sm:flex-1')}
            variant="rectangular"
            animation={animation}
          />
        </div>
      </div>
    </div>
  );
};

ServiceCardSkeleton.displayName = 'ServiceCardSkeleton';
